import gsap from 'gsap';
import { prefersReducedMotion } from '../utils';

// Interactive element that triggers the flyout menu.
const TRIGGER_CLASS = '.js--flyout__trigger';

// Content of flyout.
const CONTENT_CLASS = '.js--flyout__content';

// Optional focus target once flyout is open.
const TARGET_CLASS = '.js--flyout__target';

function appendAfter(sibling, node) {
  if (sibling.nextSibling) {
    sibling.parentNode.insertBefore(node, sibling.nextSibling);
  } else {
    sibling.parentNode.appendChild(node);
  }
}

/**
 * Flyout menu accessibility.
 *
 * @see https://www.w3.org/WAI/tutorials/menus/flyout
 *
 * Expand and automatically shift focus to flyout
 * when trigger is clicked.
 */
class Flyout {
  constructor(element) {
    this.element = element;
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.clone = null;
    this.connect();
  }

  connect() {
    this.trigger = this.element.querySelector(TRIGGER_CLASS);
    if (this.trigger === null) { return; };
    
    this.content = this.element.querySelector(CONTENT_CLASS);

    this.trigger?.addEventListener('click', this.onTriggerClick);
    this.element?.addEventListener('mouseenter', this.onMouseEnter);
    this.element?.addEventListener('mouseleave', this.onMouseLeave);
  }

  /**
   * Create clone of flyout content.
   *
   * @param {Element} content
   *
   * @return {Element}
   */
  createContentClone(content) {
    const clone = content.cloneNode(true);

    clone.style.visibility = 'visible';
    clone.style.pointerEvents = 'all';
    clone.setAttribute('aria-hidden', 'false');
    clone.removeAttribute('hidden');

    return clone;
  }

  /**
   * Toggle view of flyout menu.
   *
   * @param {Element} flyout
   * @param {boolean} expand - force expand or otherwise.
   * @param {boolean} autoFocus - autofocus to target in content?
   */
  toggle(expand = null, autoFocus = false) {
    const wasExpanded = this.trigger.getAttribute('aria-expanded') !== 'false';
    const toExpand = expand === null ? !wasExpanded : expand;

    this.trigger.setAttribute('aria-expanded', toExpand);
    this.element.classList.toggle('expanded', toExpand);

    if (!wasExpanded && toExpand) {
      /**
       * Original content stays hidden but a clone is displayed
       * for animation purposes when the flyout opens.
       */
      if (this.clone) {
        this.clone.remove();
      }
      this.clone = this.createContentClone(this.content);

      // Close flyout if focus leaves the flyout.
      this.clone.addEventListener('focusout', this.onFocusOut);

      appendAfter(this.content, this.clone);

      if (!prefersReducedMotion) {
        gsap.to(
          this.clone,
          { 
            minHeight: `${this.content.clientHeight}px`, 
            y: [0, 0],
            duration: 0.2,
            autoAlpha: 1,
          },
        );
      }

      if (autoFocus) {
        this.clone.querySelector(TARGET_CLASS)?.focus();
      }
    }

    if (wasExpanded && !toExpand) {
      const clone = this.clone;
      clone.style.pointerEvents = '';
      
      if (prefersReducedMotion) {
        clone.remove();
      } else {
        gsap.to(
          this.clone,
          { 
            minHeight: 0,
            y: [0, 0],
            duration: 0.2,
            autoAlpha: 0,
            onComplete: () => {
              this.clone.remove();
            },
          },
        );
      }
    }
  }

  onMouseEnter() {
    this.toggle(true);
  }

  onMouseLeave() {
    this.toggle(false);
  }

  onTriggerClick() {
    this.toggle(null, true);
  }

  /**
   * Toggle off flyout menu when focus moves out
   * of flyout.
   *
   * @param {Event} event
   */
  onFocusOut(event) {
    if (!this.clone.contains(event.relatedTarget)) {
      this.toggle(false);
    }
  }
}

export default Flyout;